import { ReactNode } from "react";
import styled from "styled-components";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { monolithMoment } from "@/utils/date-format";
import { Version } from "../note-history.types";

interface RestoreNoteModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  isRestoring: boolean;
  selectedVersion: Version | null;
  noteTitle: string;
  noteCreatedOn?: string;
  userName: string;
}

export const RestoreNoteModal: React.FC<RestoreNoteModalProps> = ({
  show,
  onClose,
  onConfirm,
  onCancel,
  isRestoring,
  selectedVersion,
  noteTitle,
  noteCreatedOn,
  userName,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  if (!selectedVersion) return null;

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Restore Note Version</Modal.Title>

      <WarningMessage>
        Are you sure you want to restore this note to a previous version?
      </WarningMessage>

      <InfoMessage>
        <strong>Warning:</strong> This will replace the current content of "
        {noteCreatedOn
          ? monolithMoment({
              timestamp: noteCreatedOn,
              format: "MMMM DD, YYYY HH:mm:ss",
            })
          : noteTitle}
        " with the content from the selected version. This action cannot be
        undone.
      </InfoMessage>

      <VersionDetails>
        <DetailItem
          label="Restoring To"
          value={monolithMoment({
            timestamp: selectedVersion.created_on,
            includeTime: true,
            includeZone: true,
          })}
        />
        <DetailItem label="Created By" value={userName || "Unknown User"} />
        <DetailItem
          label="Version ID"
          value={<VersionId>{selectedVersion.uuid}</VersionId>}
        />
        <DetailItem
          label="Content Preview"
          value={
            <ContentPreview>
              {selectedVersion.html
                ? selectedVersion.html
                    .replace(/<[^>]*>/g, "")
                    .substring(0, 100) + "..."
                : "No content preview available"}
            </ContentPreview>
          }
        />
      </VersionDetails>

      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={isRestoring}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="orange"
          variant="contained"
          onClick={handleConfirm}
          disabled={isRestoring}
          loading={isRestoring}
        >
          {isRestoring ? "Restoring..." : "Restore This Version"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

const WarningMessage = styled.div`
  margin: 10px 0px;
  font-size: 16px;
  font-weight: 500;
`;

const InfoMessage = styled.div`
  margin: 15px 0px;
  padding: 12px;
  background-color: ${({ theme }) => theme.palette.warning.light};
  border: 1px solid ${({ theme }) => theme.palette.warning.main};
  border-radius: 4px;
  color: ${({ theme }) => theme.palette.warning.contrastText};
  font-size: 14px;
  line-height: 1.4;
`;

const VersionDetails = styled.div`
  margin: 20px 0px;
`;

const VersionId = styled.code`
  font-family: monospace;
  font-size: 12px;
  background-color: ${({ theme }) => theme.palette.action.hover};
  padding: 2px 4px;
  border-radius: 3px;
`;

const ContentPreview = styled.div`
  font-style: italic;
  color: ${({ theme }) => theme.palette.text.secondary};
  max-width: 400px;
  word-wrap: break-word;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  width: 125px;
  min-width: 125px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const DetailValue = styled.div`
  white-space: pre-wrap;
`;

interface DetailItemProps {
  className?: string;
  label: string;
  value: ReactNode;
  labelProps?: Record<string, unknown>;
  valueProps?: Record<string, unknown>;
}

const DetailItem = styled<React.FC<DetailItemProps>>(
  ({ className, label, value, labelProps, valueProps, ...props }) => {
    return (
      <div className={className + " detail-item"} {...props}>
        <DetailLabel className={"detail-label"} {...labelProps}>
          {label}
        </DetailLabel>
        <DetailValue className={"detail-value"} {...valueProps}>
          {value}
        </DetailValue>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-bottom: 8px;
`;
